import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { BuildingOfficeIcon, PlusIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import api from '@/services/api';
import { useHierarchyOperations } from '@/hooks/useHierarchyOperations';
import { HierarchyActionModals } from '@/components/hierarchy/HierarchyActionModals';
import { HierarchyNodeActions } from '@/components/hierarchy/HierarchyNodeActions';
import { HierarchyImportButton } from '@/components/hierarchy/HierarchyImportButton';

interface HierarchyNode {
  id: string;
  name: string;
  level_type: string;
  level: number;
  parent_id?: string;
  status: string;
  children?: HierarchyNode[];
  expanded?: boolean;
}

// Removed TenantExtension interface - functionality not needed

const BusinessHierarchyPage: React.FC = () => {
  const { t } = useLanguage();
  const [hierarchy, setHierarchy] = useState<HierarchyNode[]>([]);
  // Removed tenantExtensions - functionality not needed
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  // Removed showMoveForm as it's no longer used
  const [draggedNode, setDraggedNode] = useState<HierarchyNode | null>(null);
  const [dragOverNode, setDragOverNode] = useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    level_type: 'BUSINESS_GROUP',
    parent_id: '',
    level: 1,
    status: 'ACTIVE'
  });
  // Removed moveData as it's no longer used (replaced by drag and drop)

  const loadHierarchy = async (): Promise<void> => {
    try {
      setLoading(true);
      // Use new tenant-aware API endpoint
      const response = await api.get('/hierarchy/business');
      const data = response.data.data || response.data;
      const treeData = buildHierarchyTree(data);
      setHierarchy(treeData);
    } catch (err) {
      setError(t('hierarchy.business.loadError'));
      console.error('Error loading hierarchy:', err);
    } finally {
      setLoading(false);
    }
  };

  // Use the common hierarchy operations hook
  const {
    selectedNode,
    setSelectedNode,
    handleDeleteClick,
  } = useHierarchyOperations({
    hierarchyType: 'business',
    onRefresh: loadHierarchy,
    onError: setError,
  });

  useEffect(() => {
    loadHierarchy();
  }, []);

  const buildHierarchyTree = (data: any[]): HierarchyNode[] => {
    const map = new Map();
    const roots: HierarchyNode[] = [];

    data.forEach(item => {
      map.set(item.id, { ...item, children: [], expanded: false });
    });

    data.forEach(item => {
      const node = map.get(item.id);
      if (item.parent_id && map.has(item.parent_id)) {
        const parent = map.get(item.parent_id);
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots;
  };

  const toggleNode = (nodeId: string, nodes: HierarchyNode[]): HierarchyNode[] => {
    return nodes.map(node => {
      if (node.id === nodeId) {
        return { ...node, expanded: !node.expanded };
      }
      if (node.children) {
        return { ...node, children: toggleNode(nodeId, node.children) };
      }
      return node;
    });
  };

  const handleToggleNode = (nodeId: string) => {
    setHierarchy(prev => toggleNode(nodeId, prev));
  };

  // Helper function to determine child level type based on parent
  const getChildLevelType = (parentLevelType: string): string => {
    const levelTypeMap: { [key: string]: string } = {
      'WORLDWIDE': 'BUSINESS_GROUP',
      'BUSINESS_GROUP': 'BUSINESS_ENTITY', 
      'BUSINESS_ENTITY': 'BUSINESS_LOCATION',
      'BUSINESS_LOCATION': 'BUSINESS_LOCATION' // Keep same type for deeper levels
    };
    return levelTypeMap[parentLevelType] || 'BUSINESS_ENTITY';
  };

  const handleAddNode = () => {
    const childLevelType = selectedNode ? getChildLevelType(selectedNode.level_type) : 'BUSINESS_GROUP';
    
    setFormData({
      name: '',
      level_type: childLevelType,
      parent_id: selectedNode?.id || '',
      level: (selectedNode?.level || 0) + 1,
      status: 'ACTIVE'
    });
    setShowAddForm(true);
    setShowEditForm(false);
  };

  const handleEditNode = (node: HierarchyNode) => {
    setSelectedNode(node);
    setFormData({
      name: node.name || '',
      level_type: node.level_type || 'BUSINESS_GROUP',
      parent_id: node.parent_id || '',
      level: node.level || 1,
      status: node.status || 'ACTIVE'
    });
    setShowEditForm(true);
    setShowAddForm(false);
  };

  const handleCopyNode = async (node: HierarchyNode) => {
    try {
      // Create a new node with the same properties but a different name
      const copyName = `${node.name} (Copy)`;
      const response = await api.post('/hierarchy/business', {
        name: copyName,
        level_type: node.level_type,
        parent_id: node.parent_id,
        level: node.level,
        status: 'ACTIVE'
      });
      
      if (response.status === 201) {
        await loadHierarchy();
        setSelectedNode(null);
      }
    } catch (err) {
      setError(t('hierarchy.business.copyError'));
      console.error('Error copying node:', err);
    }
  };

  // Removed handleMoveNode as it's no longer used (replaced by drag and drop)

  // Drag and Drop Handlers
  const handleDragStart = (e: React.DragEvent, node: HierarchyNode) => {
    setDraggedNode(node);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', node.id);
  };

  const handleDragOver = (e: React.DragEvent, node: HierarchyNode) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverNode(node.id);
    
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    
    // Auto-expand the node if it has children after 2 seconds of hovering
    if (node.children && node.children.length > 0 && !node.expanded) {
      const timeout = setTimeout(() => {
        const updatedHierarchy = expandNodeInHierarchy(hierarchy, node.id);
        setHierarchy(updatedHierarchy);
      }, 2000);
      setHoverTimeout(timeout);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're actually leaving the element
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverNode(null);
      // Clear the hover timeout when leaving the element
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }
    }
  };

  const handleDrop = async (e: React.DragEvent, targetNode: HierarchyNode) => {
    e.preventDefault();
    
    if (!draggedNode || draggedNode.id === targetNode.id) {
      setDraggedNode(null);
      setDragOverNode(null);
      return;
    }

    // Prevent dropping a node onto its own descendant
    if (isDescendant(draggedNode, targetNode)) {
      setError(t('hierarchy.business.moveToDescendantError'));
      setDraggedNode(null);
      setDragOverNode(null);
      return;
    }

    try {
      const response = await api.put(`/hierarchy/business/${draggedNode.id}/parent`, {
        newParentId: targetNode.id
      });
      
      if (response.status === 200) {
        // Clear any pending hover timeout
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          setHoverTimeout(null);
        }
        
        // Reload the hierarchy to get the updated structure with the moved node
        await loadHierarchy();
        
        // Re-expand the target node after reload to show the moved node
        setTimeout(() => {
          const finalHierarchy = expandNodeInHierarchy(hierarchy, targetNode.id);
          setHierarchy(finalHierarchy);
        }, 100);
        
        setSelectedNode(null);
      }
    } catch (err) {
      setError(t('hierarchy.business.moveError'));
      console.error('Error moving node:', err);
    }
    
    setDraggedNode(null);
    setDragOverNode(null);
  };

  const handleDragEnd = () => {
    setDraggedNode(null);
    setDragOverNode(null);
    // Clear any pending hover timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  // Helper function to expand a node in the hierarchy
  const expandNodeInHierarchy = (nodes: HierarchyNode[], nodeId: string): HierarchyNode[] => {
    return nodes.map(node => {
      if (node.id === nodeId) {
        return { ...node, expanded: true };
      }
      if (node.children) {
        return { ...node, children: expandNodeInHierarchy(node.children, nodeId) };
      }
      return node;
    });
  };

  // Helper function to check if a node is a descendant of another
  const isDescendant = (ancestor: HierarchyNode, potentialDescendant: HierarchyNode): boolean => {
    if (!ancestor.children) return false;
    
    for (const child of ancestor.children) {
      if (child.id === potentialDescendant.id) return true;
      if (isDescendant(child, potentialDescendant)) return true;
    }
    return false;
  };

  // Helper function to get available parent nodes (excluding current node and its descendants)
  const getAvailableParents = (currentNodeId?: string): HierarchyNode[] => {
    const flattenNodes = (nodes: HierarchyNode[]): HierarchyNode[] => {
      const result: HierarchyNode[] = [];
      nodes.forEach(node => {
        result.push(node);
        if (node.children) {
          result.push(...flattenNodes(node.children));
        }
      });
      return result;
    };

    const allNodes = flattenNodes(hierarchy);
    
    if (!currentNodeId) {
      return allNodes;
    }

    // Find the current node to exclude it and its descendants
    const currentNode = allNodes.find(node => node.id === currentNodeId);
    if (!currentNode) {
      return allNodes;
    }

    // Filter out the current node and its descendants
    return allNodes.filter(node => {
      if (node.id === currentNodeId) return false;
      return !isDescendant(currentNode, node);
    });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Removed platformId as it's no longer used
      
      if (showAddForm) {
        // Auto-determine level type and level based on whether it's a root or child node
        let level_type = 'BUSINESS_GROUP'; // Default for root nodes
        let level = 1; // Default for root nodes
        let parent_id = null;
        
        if (selectedNode) {
          // Adding child node - determine based on parent
          parent_id = selectedNode.id;
          level = selectedNode.level + 1;
          
          // Determine level type based on parent's level type
          switch (selectedNode.level_type) {
            case 'WORLDWIDE':
              level_type = 'BUSINESS_GROUP';
              break;
            case 'BUSINESS_GROUP':
              level_type = 'BUSINESS_ENTITY';
              break;
            case 'BUSINESS_ENTITY':
              level_type = 'BUSINESS_LOCATION';
              break;
            default:
              level_type = 'BUSINESS_LOCATION';
          }
        }
        
        const response = await api.post('/hierarchy/business', {
          name: formData.name,
          level_type: level_type,
          parent_id: parent_id,
          level: level
        });
        
        if (response.status === 201) {
          await loadHierarchy();
          setShowAddForm(false);
          setSelectedNode(null);
        }
      } else if (showEditForm && selectedNode) {
        // Check if parent has changed
        const parentChanged = selectedNode.parent_id !== formData.parent_id;
        
        // Update the node properties with all editable fields
        // Note: level_type is not updatable via the API, only name and status
        const updateResponse = await api.put(`/hierarchy/business/${selectedNode.id}`, {
          name: formData.name,
          status: formData.status
        });
        
        if (updateResponse.status === 200) {
          // If parent changed, update the parent separately
          if (parentChanged) {
            const parentResponse = await api.put(`/hierarchy/business/${selectedNode.id}/parent`, {
              newParentId: formData.parent_id || null
            });
            
            if (parentResponse.status !== 200) {
              setError('Failed to update parent node');
              return;
            }
          }
          
          await loadHierarchy();
          setShowEditForm(false);
          setSelectedNode(null);
        }
      }
      
      setFormData({ name: '', level_type: 'BUSINESS_GROUP', parent_id: '', level: 1, status: 'ACTIVE' });
    } catch (err) {
      setError(t('hierarchy.business.saveError'));
      console.error('Error saving node:', err);
    }
  };

  // Removed handleMoveSubmit as it's no longer used (replaced by drag and drop)

  const renderHierarchyNode = (node: HierarchyNode, level: number = 0) => (
    <div key={node.id} className="ml-4">
      <div 
        className={`flex items-center space-x-2 py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors ${
          selectedNode?.id === node.id ? 'bg-blue-50 border border-blue-200' : ''
        } ${
          dragOverNode === node.id ? 'bg-green-50 border-2 border-green-300' : ''
        } ${
          draggedNode?.id === node.id ? 'opacity-50' : ''
        }`}
        draggable
        onDragStart={(e) => handleDragStart(e, node)}
        onDragOver={(e) => handleDragOver(e, node)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, node)}
        onDragEnd={handleDragEnd}
      >
        <button
          onClick={() => handleToggleNode(node.id)}
          className="p-1 hover:bg-gray-200 rounded"
        >
          {node.children && node.children.length > 0 ? (
            node.expanded ? (
              <ChevronDownIcon className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 text-gray-500" />
            )
          ) : (
            <div className="w-4 h-4" />
          )}
        </button>
        
        <div className={`w-2 h-2 rounded-full ${
          node.status === 'ACTIVE' ? 'bg-green-500' : 'bg-gray-400'
        }`} />
        
        <span 
          className="flex-1 text-sm font-medium text-[var(--color-surface-text)] cursor-pointer hover:text-blue-600"
          onClick={() => setSelectedNode(node)}
        >
          {node.name}
        </span>
        
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
          {node.level_type}
        </span>
        
        <HierarchyNodeActions
          node={node}
          onEdit={handleEditNode}
          onDelete={handleDeleteClick}
          onCopy={handleCopyNode}
        />
      </div>
      
      {node.expanded && node.children && node.children.length > 0 && (
        <div className="ml-4 border-l-2 border-gray-200">
          {node.children.map(child => renderHierarchyNode(child, level + 1))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[var(--color-surface-text)]">Loading business hierarchy...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-secondary-text)]">
          {t('pages.businessHierarchy.title')}
        </h1>
        <p className="text-[var(--color-secondary-text)]">
          {t('pages.businessHierarchy.description')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Hierarchy Tree */}
        <div className="lg:col-span-2 bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <BuildingOfficeIcon className="h-6 w-6 text-[var(--color-primary-text)]" />
              <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">
                {t('pages.businessHierarchy.businessHierarchyStructure')}
              </h2>
            </div>
            <div className="flex items-center space-x-3">
              <HierarchyImportButton
                hierarchyType="BUSINESS"
                onImport={loadHierarchy}
              />
              <button
                onClick={handleAddNode}
                className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 rounded-md hover:opacity-90 transition-opacity flex items-center space-x-2"
              >
                <PlusIcon className="h-4 w-4" />
                <span>
                  {selectedNode 
                    ? `Add Child to "${selectedNode.name}"` 
                    : t('hierarchy.addNode')
                  }
                </span>
              </button>
            </div>
          </div>
          
          <div className="mb-2 text-sm text-[var(--color-surface-text)] opacity-75">
            💡 {t('hierarchy.business.tip')}
            <br />
            🖱️ {t('pages.businessHierarchy.dragDropTip')}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {hierarchy.map(node => renderHierarchyNode(node))}
          </div>
        </div>

        {/* Actions Panel */}
        <div className="space-y-6">
          {/* Selected Node Info */}
          {selectedNode && (
            <div className="bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
              <h3 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
                Selected Node
              </h3>
              <div className="space-y-2">
                <div>
                  <label className="text-sm font-medium text-[var(--color-surface-text)]">Name:</label>
                  <p className="text-sm text-[var(--color-surface-text)]">{selectedNode.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--color-surface-text)]">Type:</label>
                  <p className="text-sm text-[var(--color-surface-text)]">{selectedNode.level_type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--color-surface-text)]">Level:</label>
                  <p className="text-sm text-[var(--color-surface-text)]">{selectedNode.level}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--color-surface-text)]">Status:</label>
                  <p className="text-sm text-[var(--color-surface-text)]">{selectedNode.status}</p>
                </div>
              </div>
            </div>
          )}

          {/* Add/Edit Form */}
          {(showAddForm || showEditForm) && (
            <div className="bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
              <h3 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
{t(showAddForm ? 'hierarchy.business.addNewNode' : 'hierarchy.business.editNode')}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name Field */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('hierarchy.business.enterNodeName')}
                    required
                    autoFocus
                  />
                </div>

                {/* Level Type Field */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                    Type
                  </label>
                  <select
                    value={formData.level_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, level_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="WORLDWIDE">Worldwide</option>
                    <option value="BUSINESS_GROUP">Business Group</option>
                    <option value="BUSINESS_ENTITY">Business Entity</option>
                    <option value="BUSINESS_LOCATION">Business Location</option>
                  </select>
                </div>


                {/* Parent Field - Only show in edit mode */}
                {showEditForm && (
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                      Parent Node
                    </label>
                    <select
                      value={formData.parent_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, parent_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No Parent (Root Level)</option>
                      {getAvailableParents(selectedNode?.id).map(node => {
                        const level = typeof node.level === 'number' ? node.level : (parseInt(String(node.level || '1'), 10) || 1);
                        const repeatCount = Math.max(0, level - 1);
                        return (
                          <option key={node.id} value={node.id}>
                            {'  '.repeat(repeatCount)}{node.name} ({node.level_type || 'BUSINESS_GROUP'})
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {/* Status Field */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="flex-1 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 rounded-md hover:opacity-90 transition-opacity"
                  >
                    {showAddForm ? 'Add' : 'Update'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setShowEditForm(false);
                      setSelectedNode(null);
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}


          {/* Common Hierarchy Action Modals */}
          <HierarchyActionModals
            selectedNode={selectedNode}
            hierarchy={hierarchy}
          />

          {/* Removed Tenant Extensions section - functionality not needed */}
        </div>
      </div>
    </div>
  );
};

export default BusinessHierarchyPage;
